// backend/live_web/src/pages/SessionsListPage.tsx
/**
 * Every patient voice session, newest first, in the eval dashboard's worklist frame: one card,
 * the filters as the heading's controls, the table printed full-bleed, the pager as its footer.
 *
 * Filters live in the URL like every other view state, so a filtered list is a link. Email and
 * status filter on the server; urgency filters the rows of the current page only, which the
 * caption says whenever it is set. The first page refreshes every 5 seconds without dimming, so
 * a new session appears in place.
 */

import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, ChevronLeft, ChevronRight } from "lucide-react";
import type { ReactNode } from "react";
import { Navigate, useSearchParams } from "react-router-dom";

import { listSessions } from "../api/admin";
import { ApiError } from "../api/client";
import type { AdminSessionListItem } from "../api/types";
import SessionsTable from "../components/SessionsTable";
import Button from "../components/ui/Button";
import { Card, CardHeading } from "../components/ui/Card";
import EmptyState from "../components/ui/EmptyState";
import ErrorState from "../components/ui/ErrorState";
import LoadingState from "../components/ui/LoadingState";
import SearchInput from "../components/ui/SearchInput";
import Select, { type SelectOption } from "../components/ui/Select";
import { count } from "../lib/format";

type StatusFilter = "all" | "active" | "completed" | "abandoned";
type UrgencyFilter = "all" | "urgent" | "not_urgent";

const STATUS_OPTIONS: readonly SelectOption<StatusFilter>[] = [
  { value: "all", label: "All statuses" },
  { value: "active", label: "Active" },
  { value: "completed", label: "Completed" },
  { value: "abandoned", label: "Abandoned" },
];

const URGENCY_OPTIONS: readonly SelectOption<UrgencyFilter>[] = [
  { value: "all", label: "All urgencies" },
  { value: "urgent", label: "Urgent" },
  { value: "not_urgent", label: "Not urgent" },
];

function asStatus(value: string | null): StatusFilter {
  return value === "active" || value === "completed" || value === "abandoned" ? value : "all";
}

function asUrgency(value: string | null): UrgencyFilter {
  return value === "urgent" || value === "not_urgent" ? value : "all";
}

/** The urgency filter, applied to the page the server returned. */
function matchesUrgency(item: AdminSessionListItem, urgency: UrgencyFilter): boolean {
  if (urgency === "urgent") {
    return item.urgency_level === "urgent" || item.urgency_level === "emergency";
  }
  if (urgency === "not_urgent") {
    return item.urgency_level === "non_urgent" || item.urgency_level === "semi_urgent";
  }
  return true;
}

/** A 36px round step control, as the eval dashboard's pager draws them. */
function PageButton({
  label,
  onClick,
  children,
}: {
  label: string;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      title={label}
      className="grid h-9 w-9 place-items-center rounded-pill border border-hairline-2 bg-surface text-ink-2 transition-colors hover:bg-surface-muted hover:text-ink"
    >
      {children}
    </button>
  );
}

/**
 * A refresh that failed after the list had loaded: the rows stay, but no longer count as live.
 * Says so above them, e.g. when the backend is down or the sign-in has expired.
 */
function RefreshFailed({ error, onRetry }: { error: unknown; onRetry: () => void }) {
  const status = error instanceof ApiError ? ` (HTTP ${error.status})` : "";
  return (
    <div
      className="flex flex-wrap items-center gap-x-3 gap-y-2 border-b border-hairline px-7 py-3 text-body-sm text-ink-2"
      role="alert"
    >
      <AlertTriangle size={14} className="text-[color:var(--acuity-4-fg)]" aria-hidden />
      <span>The last refresh failed{status}, so this list may be out of date.</span>
      <Button variant="outline" size="sm" onClick={onRetry}>
        Try again
      </Button>
    </div>
  );
}

export default function SessionsListPage() {
  const [params, setParams] = useSearchParams();

  const email = params.get("email") ?? "";
  const status = asStatus(params.get("status"));
  const urgency = asUrgency(params.get("urgency"));
  const cursor = params.get("cursor") ?? undefined;

  const query = useQuery({
    queryKey: ["admin-sessions", { email, status, cursor }],
    queryFn: () =>
      listSessions({
        email: email || undefined,
        status: status === "all" ? undefined : status,
        cursor,
        limit: 50,
      }),
    refetchInterval: cursor ? false : 5000,
    refetchIntervalInBackground: false,
  });

  if (query.isError && query.error instanceof ApiError && query.error.status === 403) {
    return <Navigate to="/not-authorized" replace />;
  }

  function updateParam(key: string, value: string) {
    const next = new URLSearchParams(params);
    if (value && value !== "all") next.set(key, value);
    else next.delete(key);
    next.delete("cursor");
    setParams(next, { replace: true });
  }

  function goTo(nextCursor: string | null) {
    const next = new URLSearchParams(params);
    if (nextCursor) next.set("cursor", nextCursor);
    else next.delete("cursor");
    setParams(next, { replace: true });
  }

  const items = (query.data?.items ?? []).filter((item) => matchesUrgency(item, urgency));
  const fetched = query.data?.items.length ?? 0;
  const filtered = email !== "" || status !== "all" || urgency !== "all";

  const caption =
    urgency === "all"
      ? `Patient voice sessions, newest first${cursor ? "" : " · refreshes every 5 seconds"}`
      : `${count(items.length)} of the ${count(fetched)} sessions on this page match the urgency — it filters this page only, not every session`;

  return (
    <Card>
      <CardHeading
        title="Sessions"
        caption={caption}
        actions={
          <>
            <SearchInput
              value={email}
              onChange={(value) => updateParam("email", value)}
              label="Email contains"
              placeholder="Email contains…"
              className="w-[16rem]"
            />
            <Select
              value={urgency}
              onChange={(value) => updateParam("urgency", value)}
              options={URGENCY_OPTIONS}
              label="Urgency"
              className="w-[11rem]"
            />
            <Select
              value={status}
              onChange={(value) => updateParam("status", value)}
              options={STATUS_OPTIONS}
              label="Status"
              className="w-[11rem]"
            />
          </>
        }
      />

      {query.data === undefined ? (
        query.isError ? (
          <ErrorState error={query.error} onRetry={() => void query.refetch()} />
        ) : (
          <LoadingState label="Loading the sessions…" />
        )
      ) : (
        <>
          {query.isError ? (
            <RefreshFailed error={query.error} onRetry={() => void query.refetch()} />
          ) : null}
          <SessionsTable
            items={items}
            empty={
              <EmptyState
                title={filtered ? "No session matches these filters" : "No sessions yet"}
                message={
                  filtered
                    ? "Clear a filter to widen the list."
                    : "A session appears here as soon as a patient starts a consultation."
                }
                action={
                  filtered ? (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setParams(new URLSearchParams(), { replace: true })}
                    >
                      Clear the filters
                    </Button>
                  ) : undefined
                }
              />
            }
          />
          <div className="hairline flex flex-wrap items-center justify-between gap-x-6 gap-y-2 px-7 py-4">
            <p className="text-cell text-ink-3" aria-live="polite">
              Showing <span className="text-ink">{count(fetched)}</span> session
              {fetched === 1 ? "" : "s"} on this page
              {cursor ? " · not the newest" : ""}
            </p>
            <div className="flex items-center gap-2" role="group" aria-label="Page">
              {cursor ? (
                <PageButton label="Back to the newest sessions" onClick={() => goTo(null)}>
                  <ChevronLeft size={16} aria-hidden />
                </PageButton>
              ) : null}
              {query.data.next_cursor ? (
                <PageButton label="Next page" onClick={() => goTo(query.data.next_cursor)}>
                  <ChevronRight size={16} aria-hidden />
                </PageButton>
              ) : null}
            </div>
          </div>
        </>
      )}
    </Card>
  );
}
